import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import type { Portfolio, AccountPnl } from "@/lib/types";
import { fmtSek, fmtSekSigned, fmtPct, fmtShares, fmtPrice } from "@/lib/format";
import { cn } from "@/lib/utils";
import { TrendingUp, TrendingDown } from "lucide-react";

function gain(v: number) {
  return v >= 0 ? "text-emerald-500" : "text-red-500";
}

/** Gain/loss line: signed kr + percentage with a trend icon. */
function PnlLine({ unrealized, pct, large }: { unrealized: number; pct: number; large?: boolean }) {
  const Icon = unrealized >= 0 ? TrendingUp : TrendingDown;
  return (
    <div className={cn("flex items-center gap-1.5", gain(unrealized))}>
      <Icon className={large ? "size-4" : "size-3.5"} />
      <span className={cn("font-semibold tabular-nums", large ? "text-base" : "text-sm")}>
        {fmtSekSigned(unrealized)}
      </span>
      <span className={cn("tabular-nums opacity-90", large ? "text-sm" : "text-xs")}>{fmtPct(pct)}</span>
    </div>
  );
}

interface ValueCardProps {
  label: string;
  subtitle: string;
  marketValue: number;
  cost: number;
  unrealized: number;
  unrealizedPct: number;
  large?: boolean;
  highlight?: boolean;
}

function ValueCard({ label, subtitle, marketValue, cost, unrealized, unrealizedPct, large, highlight }: ValueCardProps) {
  return (
    <Card className={highlight ? "border-primary/20 bg-gradient-to-br from-card to-muted/30" : undefined}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between text-base">
          {label}
          <span className="text-xs font-normal text-muted-foreground">{subtitle}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {/* Hero: current market value of the holding. */}
        <div className="text-xs text-muted-foreground">Marknadsvärde</div>
        <div className={cn("font-bold tabular-nums leading-none", large ? "text-3xl" : "text-2xl")}>
          {fmtSek(marketValue)}
        </div>
        <PnlLine unrealized={unrealized} pct={unrealizedPct} large={large} />
        <div className="pt-1 text-xs text-muted-foreground">
          Anskaffning <span className="tabular-nums text-foreground">{fmtSek(cost)}</span>
        </div>
      </CardContent>
    </Card>
  );
}

export function PortfolioCards({ portfolio, loading }: { portfolio: Portfolio | null; loading: boolean }) {
  if (loading && !portfolio) {
    return (
      <div className="grid gap-4 md:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-40" />
        ))}
      </div>
    );
  }
  if (!portfolio) return null;

  return (
    <div className="grid gap-4 md:grid-cols-3">
      <ValueCard
        label="Totalt"
        subtitle={`${fmtShares(portfolio.total.shares)} st`}
        marketValue={portfolio.total.marketValue}
        cost={portfolio.total.cost}
        unrealized={portfolio.total.unrealized}
        unrealizedPct={portfolio.total.unrealizedPct}
        large
        highlight
      />
      {portfolio.accounts.map((a: AccountPnl) => (
        <ValueCard
          key={a.id}
          label={a.label}
          subtitle={`${fmtShares(a.shares)} st @ ${fmtPrice(a.avgCost)}`}
          marketValue={a.marketValue}
          cost={a.cost}
          unrealized={a.unrealized}
          unrealizedPct={a.unrealizedPct}
        />
      ))}
    </div>
  );
}
